/**
 * Global Search (Search01) — framework-free helpers.
 *
 * Kept pure so the min-length rule, the stored-type validation and the response normaliser are
 * unit-tested without a component-render harness (see __tests__), matching the repo convention.
 */
import type {
  BucketKey,
  GlobalSearchResponse,
  ResultBucket,
  ResultItem,
  ResultItemType,
  SearchType,
} from './types'

/** Minimum characters before a search fires (contract: <2 chars → empty 200, no request). */
export const MIN_QUERY_LENGTH = 2

/**
 * Legacy localStorage key for the previously-sticky type selector.
 * The header no longer persists the selection (PR-I-54: always start at 'all' and reset on
 * navigation). Kept only so `useSearchType` can proactively remove stale values from returning
 * users' browsers. Do not reintroduce reads.
 */
export const SEARCH_TYPE_STORAGE_KEY = 'nobilis.search.type'

/** The valid selector/param values, in display order (All first). */
export const SEARCH_TYPES: SearchType[] = [
  'all',
  'members',
  'experiences',
  'forums',
  'mastermind_circles',
  'expertise',
]

/** Aggregate bucket keys (camelCase on the wire), in the same order as SEARCH_TYPES minus `all`. */
export const BUCKET_KEYS: BucketKey[] = [
  'members',
  'experiences',
  'forums',
  'mastermindCircles',
  'expertise',
]

/** True when the (trimmed) query is long enough to search. */
export const meetsMinLength = (query: string | null | undefined): boolean =>
  (query ?? '').trim().length >= MIN_QUERY_LENGTH

/** Type guard for a value being a known SearchType. */
export const isValidSearchType = (value: unknown): value is SearchType =>
  typeof value === 'string' && (SEARCH_TYPES as string[]).includes(value)

/** Coerce any raw value to a valid SearchType, falling back to 'all'. Never throws. */
export const coerceSearchType = (raw: unknown): SearchType =>
  isValidSearchType(raw) ? raw : 'all'

/** An empty, well-formed bucket. */
const emptyBucket = <T extends ResultItem>(): ResultBucket<T> => ({total: 0, items: []})

/** Coerce one raw bucket into a well-formed { total, items[] }, tolerating missing/garbage input. */
const normalizeBucket = <T extends ResultItem>(raw: any): ResultBucket<T> => {
  if (!raw || typeof raw !== 'object') return emptyBucket<T>()
  const items = Array.isArray(raw.items) ? (raw.items as T[]) : []
  const total = typeof raw.total === 'number' ? raw.total : items.length
  return {total, items}
}

/**
 * Normalise a raw aggregate response into a fully-formed GlobalSearchResponse. Every bucket is
 * guaranteed present with an array of items, so the UI never has to null-check bucket shape.
 * Tolerates a null/garbage payload (returns all-empty buckets).
 */
export const normalizeGlobalResponse = (raw: any): GlobalSearchResponse => {
  const buckets = raw && typeof raw === 'object' ? raw.buckets : undefined
  return {
    query: raw && typeof raw.query === 'string' ? raw.query : '',
    buckets: {
      members: normalizeBucket(buckets?.members),
      experiences: normalizeBucket(buckets?.experiences),
      forums: normalizeBucket(buckets?.forums),
      mastermindCircles: normalizeBucket(buckets?.mastermindCircles),
      expertise: normalizeBucket(buckets?.expertise),
    },
  }
}

/** Max result rows shown per group, and across the whole dropdown (GS-02). */
export const MAX_ROWS_PER_GROUP = 5
export const MAX_DROPDOWN_ROWS = 30

/** A dropdown group ready to render: which bucket, the type to deep-link to, its total, its rows. */
export interface DropdownGroup {
  bucketKey: BucketKey
  searchType: Exclude<SearchType, 'all'>
  itemType: ResultItemType
  total: number
  items: ResultItem[]
}

/** Bucket → (deep-link type, item discriminant), in the requested display order:
 *  Members → Experiences → Expert → Forum.
 *
 *  Mastermind Circles is intentionally omitted so it never surfaces in the header dropdown or
 *  the "See all" total, even when the backend still returns that bucket in a `type=all`
 *  response. The normalized shape and per-item helpers (labelForType/iconForType) still know
 *  about mastermind_circles, so any future re-enable is a one-line change. */
const GROUP_DEFS: {bucketKey: BucketKey; searchType: Exclude<SearchType, 'all'>; itemType: ResultItemType}[] = [
  {bucketKey: 'members', searchType: 'members', itemType: 'member'},
  {bucketKey: 'experiences', searchType: 'experiences', itemType: 'experience'},
  {bucketKey: 'expertise', searchType: 'expertise', itemType: 'expertise'},
  {bucketKey: 'forums', searchType: 'forums', itemType: 'forum'},
]

/** Sum of every bucket's reported total (used for the empty-state decision). */
export const totalResults = (response: GlobalSearchResponse | null): number => {
  if (!response) return 0
  return GROUP_DEFS.reduce((sum, d) => sum + (response.buckets[d.bucketKey]?.total ?? 0), 0)
}

/**
 * Build the ordered, capped groups for the dropdown:
 *  - at most MAX_ROWS_PER_GROUP rows per group,
 *  - groups with no rows hidden,
 *  - groups ordered by hit count (total) descending (ties keep default order — stable sort),
 *  - overall rows capped at MAX_DROPDOWN_ROWS.
 */
export const buildDropdownGroups = (response: GlobalSearchResponse | null): DropdownGroup[] => {
  if (!response) return []
  const groups: DropdownGroup[] = GROUP_DEFS.map((d) => {
    const bucket = response.buckets[d.bucketKey]
    const items = Array.isArray(bucket?.items) ? bucket.items.slice(0, MAX_ROWS_PER_GROUP) : []
    return {
      bucketKey: d.bucketKey,
      searchType: d.searchType,
      itemType: d.itemType,
      total: bucket?.total ?? items.length,
      items,
    }
  }).filter((g) => g.items.length > 0)

  // Display order is fixed (Members → Experiences → Expert → Forum) — GROUP_DEFS declares it,
  // and we intentionally do NOT re-sort by hit count.

  // Overall cap across groups.
  let remaining = MAX_DROPDOWN_ROWS
  const capped: DropdownGroup[] = []
  for (const g of groups) {
    if (remaining <= 0) break
    const items = g.items.slice(0, remaining)
    remaining -= items.length
    capped.push({...g, items})
  }
  return capped
}

/** Flat ordered list of rows across groups — the keyboard-navigation model. */
export const flattenGroupRows = (groups: DropdownGroup[]): ResultItem[] =>
  groups.reduce<ResultItem[]>((acc, g) => acc.concat(g.items), [])

/**
 * Keyboard active-row reducer for the dropdown (a11y). Given the current active index (-1 = none),
 * a key and the number of rows, returns the next active index. Arrow keys wrap; Home/End jump to
 * the ends; any other key leaves the index unchanged. Pure, so it is unit-tested directly.
 */
export const nextActiveIndex = (current: number, key: string, count: number): number => {
  if (count <= 0) return -1
  switch (key) {
    case 'ArrowDown':
      return current + 1 > count - 1 ? 0 : current + 1
    case 'ArrowUp':
      return current - 1 < 0 ? count - 1 : current - 1
    case 'Home':
      return 0
    case 'End':
      return count - 1
    default:
      return current
  }
}

/**
 * A minimal debounce. Pure/reusable and independently unit-tested; `useDebouncedValue` builds on
 * it so the timing logic is covered without rendering a component.
 */
export function debounce<A extends any[]>(fn: (...args: A) => void, delay: number) {
  let timer: ReturnType<typeof setTimeout> | null = null
  const debounced = (...args: A) => {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      timer = null
      fn(...args)
    }, delay)
  }
  debounced.cancel = () => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }
  return debounced
}
